import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UtcToLocalPipe } from '../../pipes/utc-to-local.pipe';
import {
  StateMessageComponent,
} from '@groundworks/ngx-groundworks-ui';
import { ModalService } from '../../services/modal.service';
import { TouchpointService } from '../../services/touchpoint.service';
import {
  CreateTouchpointRequest,
  TouchpointInfo,
  TouchpointType,
} from '../../sdk/sdk';

@Component({
  selector: 'app-touchpoints',
  imports: [DatePipe, FormsModule, StateMessageComponent, UtcToLocalPipe],
  templateUrl: './touchpoints.component.html',
  styleUrl: './touchpoints.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TouchpointsComponent implements OnInit {
  private readonly touchpointService = inject(TouchpointService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  readonly opportunityId = input.required<string>();

  readonly touchpoints = signal<TouchpointInfo[]>([]);
  readonly isLoading = signal(false);
  readonly hasError = signal(false);
  readonly isCollapsed = signal(false);
  readonly selectedType = signal<TouchpointType>(TouchpointType.Production);
  readonly newNote = signal('');
  readonly isSubmitting = signal(false);

  readonly characterCount = computed(() => this.newNote().length);
  readonly maxLength = 2000;
  readonly isNoteValid = computed(
    () =>
      this.newNote().trim().length > 0 &&
      this.characterCount() <= this.maxLength
  );

  readonly isSalesSelected = computed(() => this.selectedType() === TouchpointType.Sales);

  readonly filteredTouchpoints = computed(() => {
    const type = this.selectedType();
    return this.touchpoints().filter((t) => t.type === type);
  });

  readonly TouchpointType = TouchpointType;

  ngOnInit(): void {
    this.loadTouchpoints();
  }

  toggleCollapsed(): void {
    this.isCollapsed.update((v) => !v);
  }

  onNoteInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    if (value.length <= this.maxLength) {
      this.newNote.set(value);
    }
  }

  addNote(): void {
    if (!this.isNoteValid() || this.isSubmitting()) {
      return;
    }

    this.isSubmitting.set(true);

    const request = new CreateTouchpointRequest({
      opportunityId: this.opportunityId(),
      content: this.newNote().trim(),
      type: TouchpointType.Production,
      sourceName: undefined,
    });

    this.touchpointService
      .createTouchpoint(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (touchpoint) => {
          this.touchpoints.update((list) => [touchpoint, ...list]);
          this.newNote.set('');
          this.isSubmitting.set(false);
        },
        error: (error) => {
          console.error('Error creating touchpoint:', error);
          this.isSubmitting.set(false);
          this.modalService.openErrorModal(
            'Error Adding Note',
            this.getErrorMessage(error, 'An unexpected error occurred while adding the note. Please try again.')
          );
        },
      });
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    const err = error as { status?: number; error?: { detail?: string }; message?: string };
    const detail = err?.error?.detail;
    switch (err?.status) {
      case 403:
        return 'The current logged in user is not allowed to perform this action.';
      case 404:
        return detail || 'The requested resource could not be found.';
      case 500:
        return detail || 'An internal server error occurred.';
      default:
        return detail || err?.message || fallback;
    }
  }

  loadTouchpoints(): void {
    this.isLoading.set(true);
    this.hasError.set(false);

    this.touchpointService
      .getTouchpoints(this.opportunityId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (touchpoints) => {
          this.touchpoints.set(touchpoints);
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('Error loading touchpoints:', error);
          this.hasError.set(true);
          this.isLoading.set(false);
          this.modalService.openErrorModal(
            'Error Loading Touchpoints',
            this.getErrorMessage(error, 'An unexpected error occurred while loading touchpoints. Please try again.')
          );
        },
      });
  }
}
